import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'wouter';
import { UserPlus, Shield, Crown, User, Trash2 } from 'lucide-react';
import { Layout } from '../components/Layout';
import { Modal } from '../components/Modal';
import { api } from '../lib/api';
import { useAuth } from '../hooks/use-auth';

const ROLE_ICONS: Record<string, any> = { owner: Crown, admin: Shield, member: User };
const ROLE_COLORS: Record<string, string> = { owner: 'text-amber-400', admin: 'text-blue-400', member: 'text-gray-400' };

function canManageMember(callerRole: string, targetRole: string): boolean {
  if (callerRole === 'owner') return true;
  if (callerRole === 'admin' && targetRole !== 'owner') return true;
  return false;
}

function availableRoles(callerRole: string): string[] {
  if (callerRole === 'owner') return ['owner', 'admin', 'member'];
  if (callerRole === 'admin') return ['admin', 'member'];
  return [];
}

export function BandSettings() {
  const params = useParams<{ bandId: string }>();
  const bandId = Number(params.bandId);
  const [showAdd, setShowAdd] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [confirmRemove, setConfirmRemove] = useState<{ id: number; name: string } | null>(null);
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: band } = useQuery({
    queryKey: ['band', bandId],
    queryFn: () => api.bands.get(bandId),
  });

  const { data: members = [] } = useQuery({
    queryKey: ['bandMembers', bandId],
    queryFn: () => api.bands.getMembers(bandId),
  });

  const addMember = useMutation({
    mutationFn: () => api.bands.addMember(bandId, { email, role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bandMembers', bandId] });
      setShowAdd(false);
      setEmail('');
      setRole('member');
    },
  });

  const updateRole = useMutation({
    mutationFn: ({ memberId, newRole }: { memberId: number; newRole: string }) =>
      api.bands.updateMemberRole(bandId, memberId, newRole),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bandMembers', bandId] });
    },
  });

  const removeMember = useMutation({
    mutationFn: (memberId: number) => api.bands.removeMember(bandId, memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bandMembers', bandId] });
      setConfirmRemove(null);
    },
  });

  const myRole = band?.role || 'member';
  const isAdmin = myRole === 'owner' || myRole === 'admin';

  return (
    <Layout bandId={bandId} bandName={band?.name} title="Settings">
      <div className="p-4 md:p-6 max-w-2xl mx-auto space-y-6">
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">Members</h2>
            {isAdmin && (
              <button
                onClick={() => setShowAdd(true)}
                className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
              >
                <UserPlus className="w-4 h-4" />
                Add Member
              </button>
            )}
          </div>

          <div className="space-y-2">
            {members.map((m: any) => {
              const RoleIcon = ROLE_ICONS[m.role] || User;
              const canManage = canManageMember(myRole, m.role);
              const isSelf = m.userId === user?.id;
              const roles = availableRoles(myRole);

              return (
                <div key={m.id} className="flex items-center gap-3 p-3 bg-surface-900 border border-surface-700 rounded-lg">
                  {m.user?.avatarUrl ? (
                    <img src={m.user.avatarUrl} alt="" className="w-8 h-8 rounded-full" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-surface-700 flex items-center justify-center text-xs font-bold">
                      {m.user?.displayName?.[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {m.user?.displayName}
                      {isSelf && <span className="text-xs text-gray-500 ml-1">(you)</span>}
                    </p>
                    <p className="text-xs text-gray-500">{m.user?.email || ''}</p>
                  </div>

                  {canManage && !isSelf ? (
                    <div className="flex items-center gap-2">
                      <select
                        value={m.role}
                        onChange={(e) => updateRole.mutate({ memberId: m.id, newRole: e.target.value })}
                        className="bg-surface-800 border border-surface-700 rounded px-2 py-1 text-xs capitalize focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        {roles.map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => setConfirmRemove({ id: m.id, name: m.user?.displayName || 'this member' })}
                        className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded transition-colors"
                        title="Remove member"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className={`flex items-center gap-1 text-xs capitalize ${ROLE_COLORS[m.role]}`}>
                      <RoleIcon className="w-3.5 h-3.5" />
                      {m.role}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {(updateRole.isError || removeMember.isError) && (
            <p className="text-sm text-red-400 mt-2">
              {((updateRole.error || removeMember.error) as Error)?.message || 'An error occurred'}
            </p>
          )}
        </div>

        <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add Member">
          <form onSubmit={e => { e.preventDefault(); addMember.mutate(); }} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" placeholder="user@example.com" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Role</label>
              <select value={role} onChange={e => setRole(e.target.value)} className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <p className="text-xs text-gray-500">The user must have signed in to Chartz at least once.</p>
            <button type="submit" disabled={addMember.isPending} className="w-full bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-medium py-2 rounded-lg transition-colors text-sm">
              {addMember.isPending ? 'Adding...' : 'Add Member'}
            </button>
            {addMember.isError && <p className="text-sm text-red-400">{(addMember.error as Error).message}</p>}
          </form>
        </Modal>

        <Modal open={!!confirmRemove} onClose={() => setConfirmRemove(null)} title="Remove Member">
          <div className="space-y-4">
            <p className="text-sm text-gray-300">
              Are you sure you want to remove <strong>{confirmRemove?.name}</strong> from this band?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmRemove(null)}
                className="flex-1 bg-surface-700 hover:bg-surface-600 text-white font-medium py-2 rounded-lg transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => confirmRemove && removeMember.mutate(confirmRemove.id)}
                disabled={removeMember.isPending}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-medium py-2 rounded-lg transition-colors text-sm"
              >
                {removeMember.isPending ? 'Removing...' : 'Remove'}
              </button>
            </div>
            {removeMember.isError && <p className="text-sm text-red-400">{(removeMember.error as Error).message}</p>}
          </div>
        </Modal>
      </div>
    </Layout>
  );
}
